/* HGP-Turbo, text-clamp.js
   "Show more" for a block of copy that does not fit.

   Markup contract (see sections/main-car.liquid for the live use, the vehicle intro):

     <div data-clamp data-clamp-state="collapsed" style="--cd-intro-lines:4">
       <div data-clamp-text>…the FULL copy, never server-truncated. Paragraphs and bullet
                            lists are fine: see snippets/rich-copy.liquid…</div>
       <button data-clamp-btn hidden aria-expanded="false"
               data-more="Mehr anzeigen" data-less="Weniger anzeigen">
         <span data-clamp-label>Mehr anzeigen</span>
       </button>
     </div>

   The stylesheet owns the collapsed look; this file owns one decision only:
   does the copy actually overflow at the width it ended up with? It is measured in the
   browser, per block, so the same markup behaves correctly on a 360px phone and a 2560px
   monitor without a single breakpoint, and on a car whose intro is 6 words as well as one
   whose intro is 84.

   Three rules it follows:
   - The block is rendered COLLAPSED by the server, and this script only ever takes the clamp
     away. Clamping copy that already fits is invisible, so a short intro never flashes at full
     height and snaps back. Doing it the other way round would jump on nearly every car page.
   - The button is hidden until the overflow is proven. A button that expands nothing is worse
     than no button.
   - It re-measures whenever the answer can change: the viewport or the column resizes, the web
     font finally loads and reflows the copy, late images shift the layout, or the theme editor
     re-renders the section. */
(function () {
  "use strict";

  var blocks = [];
  var queued = false;

  function build(root) {
    var text = root.querySelector("[data-clamp-text]");
    var btn = root.querySelector("[data-clamp-btn]");
    if (!text || !btn) return null;

    var b = {
      root: root,
      text: text,
      btn: btn,
      label: btn.querySelector("[data-clamp-label]") || btn,
      more: btn.getAttribute("data-more") || "",
      less: btn.getAttribute("data-less") || "",
      expanded: false,   // what the reader asked for
      needed: false,     // whether the copy overflows at this width
      width: -1
    };

    btn.addEventListener("click", function () { toggle(b); });
    root.__clamp = b;
    return b;
  }

  /* Write the current state out. `needed` false means the copy fits, so the clamp comes off and
     the button stays away: the block then renders exactly as it did before this feature. */
  function apply(b) {
    var collapsed = b.needed && !b.expanded;
    b.root.setAttribute("data-clamp-state", collapsed ? "collapsed" : "expanded");
    b.btn.hidden = !b.needed;
    b.btn.setAttribute("aria-expanded", b.expanded ? "true" : "false");
    var want = b.expanded ? b.less : b.more;
    if (want && b.label.textContent !== want) b.label.textContent = want;
  }

  /* Both heights are measured directly, open and shut, rather than asking one clamped element
     how far it overflows. That keeps this independent of HOW the stylesheet collapses the
     block: a single paragraph is clamped with -webkit-line-clamp on the paragraph itself (for
     the ellipsis) and structured copy with a max-height on the container, and an overflow
     reading would have been blind to the first of those. Both writes happen inside one task,
     so the browser only ever paints the state this lands on.
     The reader's own expanded choice is put back afterwards, unless the copy now fits. */
  function measure(b) {
    var wasExpanded = b.expanded;
    b.root.setAttribute("data-clamp-state", "expanded");
    var full = b.text.getBoundingClientRect().height;
    b.root.setAttribute("data-clamp-state", "collapsed");
    var cut = b.text.getBoundingClientRect().height;
    // 1px of slack: a fractional line box must not be read as a whole hidden line.
    b.needed = (full - cut) > 1;
    b.expanded = wasExpanded && b.needed;
    apply(b);
  }

  function measureAll() {
    for (var i = 0; i < blocks.length; i++) measure(blocks[i]);
  }

  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; measureAll(); });
  }

  function toggle(b) {
    b.expanded = !b.expanded;
    apply(b);
    /* Collapsing can pull the button up past the top of the window and leave the reader looking
       at whatever now occupies that spot. Only nudged when it actually went out of view. */
    if (!b.expanded) {
      var top = b.root.getBoundingClientRect().top;
      if (top < 0) {
        try { b.root.scrollIntoView({ behavior: "smooth", block: "start" }); }
        catch (e) { b.root.scrollIntoView(); }
      }
    }
  }

  /* Width is the only thing that changes the answer. Height changes are this script's own doing
     (expanding, collapsing), and reacting to those would be a loop. */
  var ro = window.ResizeObserver ? new ResizeObserver(function (entries) {
    var relevant = false;
    for (var i = 0; i < entries.length; i++) {
      var b = entries[i].target.__clamp;
      if (!b) continue;
      var w = Math.round(entries[i].contentRect.width);
      if (w === b.width) continue;
      b.width = w;
      relevant = true;
    }
    if (relevant) schedule();
  }) : null;

  function scan() {
    var found = document.querySelectorAll("[data-clamp]");
    for (var i = 0; i < found.length; i++) {
      if (found[i].__clamp) continue;
      var b = build(found[i]);
      if (!b) continue;
      blocks.push(b);
      if (ro) ro.observe(found[i]);
    }
    measureAll();
  }

  scan();

  if (!ro) {
    var t = 0;
    window.addEventListener("resize", function () {
      clearTimeout(t);
      t = setTimeout(measureAll, 120);
    });
  }
  // orientation change resizes before the new metrics are readable on some phones
  window.addEventListener("orientationchange", function () { setTimeout(measureAll, 200); });
  // the copy reflows when the self-hosted face replaces the fallback
  if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
    document.fonts.ready.then(schedule).catch(function () {});
  }
  window.addEventListener("load", schedule);
  // theme editor: a re-rendered section brings new nodes, and unhiding one gives it a size
  document.addEventListener("shopify:section:load", scan);
  document.addEventListener("shopify:section:select", schedule);
})();
